import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { omit, uniqueId } from "lodash";
import * as Yup from "yup";
import { useFormik } from "formik";

import InputFieldText from "Components/Common/InputFieldText";
import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { utilityOptionsForUser, fireSafetyDepartments } from "common/enums";

import { generateFormData } from "utils";

import {
  useLazyGetUserQuery,
  usePostAdminRegisterMutation,
  usePutAdminProfileMutation,
} from "apis/User";
import { useGetRolesQuery } from "apis/Role";

const UserModal = ({ userId, isOpen, handleClose, t }) => {
  const [getUser, { data: { data: userData } = {} }] = useLazyGetUserQuery();
  const {
    data: { data: rolesOptions } = {},
    isLoading: isRolesOptionsLoading,
  } = useGetRolesQuery({}, true);

  const [addUser, { isLoading }] = usePostAdminRegisterMutation();
  const [updateUser, { isLoading: isUserUpdating }] =
    usePutAdminProfileMutation();

  const roles = useMemo(
    () =>
      rolesOptions
        ? rolesOptions.map(({ name, id }) => ({ label: name, value: id }))
        : [],
    [rolesOptions]
  );

  const {
    profileImage,
    signatureImage,
    firstName,
    lastName,
    userName,
    password,
    email,
  } = userData ?? {};

  const registerValidationSchema = useMemo(
    () =>
      Yup.object({
        firstName: Yup.string()
          .matches(/^[A-Za-z]+$/, "Please enter valid first name")
          // .min(2, "First name must contains more then two characters")
          .required("Please enter first name"),
        lastName: Yup.string().matches(
          /^[A-Za-z]+$/,
          "Please enter valid last name"
        ),
        // .min(2, "Last name contains at least more then two characters")
        userName: Yup.string()
          .trim()
          // .min(2, "User name contains at least more then two characters")
          .required("Please enter user name"),
        password: userId
          ? Yup.string()
          : Yup.string().required("Please enter password"),

        email: Yup.string()
          .email("Please enter valid email")
          .required("Email is required"),

        departmentRoles: Yup.array().of(
          Yup.object().shape({
            departmentId: Yup.string().required("Department is required"),
            roles: Yup.array().min(1, "Roles is required"),
          })
        ),
      }),
    [userId]
  );

  const handleUserData = async () => {
    try {
      await getUser(userId, {}, true);
    } catch (err) {}
  };

  const initialValues = useMemo(() => {
    return {
      firstName: firstName || "",
      lastName: lastName || "",
      userName: userName || "",
      password: password || "",
      email: email || "",
      departmentRoles: userData?.fireSafetyDepartmentRoles?.map(
        ({ departmentId, fireSafetyRoles: currentRoles }) => ({
          id: uniqueId(),
          departmentId,
          roles: roles.filter(({ value }) =>
            currentRoles.find(({ id }) => id === value)
          ),
        })
      ) ?? [{ departmentId: "", roles: [], id: uniqueId() }],
    };
  }, [userData, userId, roles]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: registerValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      const departmentRoles = values.departmentRoles.map(
        ({ roles, departmentId }) => ({
          roles: roles.map(({ value }) => value),
          departmentIds: [departmentId],
        })
      );
      const updatedValues = { ...values, departmentRoles };
      try {
        if (userId) {
          const formData = new FormData();
          generateFormData(
            formData,
            omit(
              {
                ...updatedValues,
                profileImageURL: profileImage ?? "",
                signatureImage: signatureImage ?? "",
              },
              "password"
            )
          );
          await updateUser(
            formData,
            {
              userId,
            },
            true
          );
        } else {
          await addUser(updatedValues, true);
        }
        handleClose();
        resetForm();
      } catch (error) {}
    },
  });

  const {
    setFieldValue,
    values: { departmentRoles },
    handleSubmit,
  } = formik;

  useEffect(() => {
    if (userId) {
      handleUserData();
    }
  }, [userId]);

  if (isRolesOptionsLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      handleClose={handleClose}
      isOpen={isOpen}
      maxWidth="md"
      title={`${userId ? "Edit" : "Add"} User`}
    >
      <Grid container columnSpacing={1} rowSpacing={1} className="mt-2">
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("First Name") + "*"}
            name="firstName"
            formik={formik}
            placeholder="Enter First Name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Last Name")}
            name="lastName"
            formik={formik}
            placeholder="Enter Last Name"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("User Name") + "*"}
            name="userName"
            formik={formik}
            placeholder="Enter User Name"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <InputFieldText
            label={t("Email") + "*"}
            name="email"
            formik={formik}
            placeholder="Enter Email"
          />
        </Grid>

        {!userId && (
          <Grid item xs={12} sm={4}>
            <InputFieldText
              label={t("Password") + "*"}
              name="password"
              formik={formik}
              type="password"
              autoComplete="new-password"
              placeholder="Enter Password"
            />
          </Grid>
        )}
        {departmentRoles.map(
          ({ roles: selectedRoles, id: currentId }, index) => (
            <Grid
              container
              columnSpacing={1}
              rowSpacing={1}
              item
              xs={12}
              key={index}
            >
              <Grid item xs={12} sm={3}>
                <SelectField
                  label={t("Department") + "*"}
                  name={`departmentRoles.${index}.departmentId`}
                  formik={formik}
                  options={fireSafetyDepartments}
                  placeholder="Select Department"
                  getOptionDisabled={({ value }) =>
                    departmentRoles.some(
                      ({ departmentId }) => departmentId === value
                    )
                  }
                />
              </Grid>
              <Grid item xs={10} sm={8}>
                <SelectField
                  label={t("Roles") + "*"}
                  name={`departmentRoles.${index}.roles`}
                  multiple
                  disableCloseOnSelect
                  renderOption={(props, option, { selected }) => {
                    return (
                      <li {...props} key={option.value}>
                        <Checkbox
                          sx={{ marginRight: "8px" }}
                          checked={selected}
                        />
                        {option.label}
                      </li>
                    );
                  }}
                  formik={formik}
                  value={selectedRoles}
                  onChange={(event, options) => {
                    if (event.key !== "Backspace" && event.key !== "Delete") {
                      setFieldValue(`departmentRoles.${index}.roles`, options);
                    }
                  }}
                  options={roles}
                  placeholder="Select roles"
                />
              </Grid>
              <Grid item xs={1}>
                <IconButton
                  onClick={() =>
                    setFieldValue(
                      "departmentRoles",
                      departmentRoles.filter(({ id }) => id !== currentId)
                    )
                  }
                  disabled={departmentRoles.length === 1}
                >
                  <CloseIcon
                    color={departmentRoles.length === 1 ? "disabled" : "error"}
                  />
                </IconButton>
              </Grid>
            </Grid>
          )
        )}

        <Grid item sm={12} className="d-flex justify-content-between">
          <Button
            variant="outlined"
            onClick={() => {
              if (utilityOptionsForUser.length > departmentRoles.length) {
                setFieldValue("departmentRoles", [
                  ...departmentRoles,
                  { departmentId: "", roles: [], id: uniqueId() },
                ]);
              }
            }}
            disabled={isUserUpdating || isLoading}
          >
            {t("Add Department")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isUserUpdating || isLoading}
            startIcon={
              (isLoading || isUserUpdating) && <CircularProgress size={20} />
            }
          >
            {t("Submit")}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

UserModal.propTypes = {
  handleClose: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(UserModal);
